import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// Custom Confirmation Modal Component - Synced with app theme
const ConfirmModal = ({ isOpen, title, message, onConfirm, onCancel, confirmText = 'Yes', cancelText = 'No', type = 'warning' }) => {
  if (!isOpen) return null;
  
  // Colors matching the app's theme (primary: #1f84f1)
  const colors = {
    warning: { bg: '#f0ad4e', hoverBg: '#ec971f', color: '#fff', titleColor: '#f0ad4e' },
    success: { bg: '#28a745', hoverBg: '#218838', color: '#fff', titleColor: '#28a745' },
    error: { bg: '#dc3545', hoverBg: '#c82333', color: '#fff', titleColor: '#dc3545' },
    info: { bg: '#1f84f1', hoverBg: '#1a70d1', color: '#fff', titleColor: '#1f84f1' }
  };
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        padding: '24px',
        maxWidth: '400px',
        width: '100%',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
        border: '1px solid #f0f0f0'
      }}>
        <h3 style={{ 
          margin: '0 0 16px 0', 
          color: colors[type].titleColor,
          fontSize: '20px',
          fontWeight: '600'
        }}>
          {title}
        </h3>
        <p style={{ 
          margin: '0 0 24px 0', 
          color: '#666',
          fontSize: '15px',
          lineHeight: '1.6',
          whiteSpace: 'pre-line'
        }}>
          {message}
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          {cancelText && (
            <button
              onClick={onCancel}
              style={{
                padding: '12px 24px',
                borderRadius: '12px',
                border: '1px solid #e0e0e0',
                backgroundColor: '#f5f5f5',
                color: '#333',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#ebebeb'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#f5f5f5'}
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={onConfirm}
            style={{
              padding: '12px 24px',
              borderRadius: '12px',
              border: 'none',
              backgroundColor: colors[type].bg,
              color: colors[type].color,
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = colors[type].hoverBg}
            onMouseOut={(e) => e.target.style.backgroundColor = colors[type].bg}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const DoctorListPage = () => {
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [approvingId, setApprovingId] = useState(null);
  
  // Maximum doctors per user
  const MAX_DOCTORS = 20;
  
  // Modal state
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    confirmText: 'Yes',
    cancelText: 'No',
    onConfirm: () => {},
    doctor: null
  });
  
  const pageSize = 5;
  // Production URL - hardcoded for HTTPS
  const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const API_BASE_URL = isDevelopment ? 'http://localhost:8000/api' : 'https://trigabantin.medupdates.in/api';

  const closeModal = () => {
    setModalConfig(prev => ({ ...prev, isOpen: false }));
  };

  const showModal = (config) => {
    setModalConfig({ ...config, isOpen: true });
  };

  const loadDoctors = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.get('/doctors/');
      setDoctors(response.data || []);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to load doctors.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = (doctor) => {
    // Show confirmation modal
    showModal({
      title: '⚠️ Confirm Approval',
      message: `Are you sure you want to approve Dr. ${doctor.name}?\n\nOnce approved, you cannot edit this doctor's details.`,
      type: 'warning',
      confirmText: 'Yes, Approve',
      cancelText: 'Cancel',
      doctor: doctor,
      onConfirm: async () => {
        closeModal();
        setApprovingId(doctor.id);
        
        try {
          await api.post(`/doctors/${doctor.id}/approve`);
          // Update local state to reflect approval
          setDoctors(prev => prev.map(d => 
            d.id === doctor.id ? { ...d, is_approved: true } : d
          ));
          // Show success modal
          showModal({
            title: '✓ Approved!',
            message: `Dr. ${doctor.name} has been approved successfully!`,
            type: 'success',
            confirmText: 'OK',
            cancelText: '',
            onConfirm: closeModal
          });
        } catch (err) {
          const message = err.response?.data?.detail || 'Failed to approve doctor.';
          showModal({
            title: '✗ Error',
            message: message,
            type: 'error',
            confirmText: 'OK',
            cancelText: '',
            onConfirm: closeModal
          });
        } finally {
          setApprovingId(null);
        }
      }
    });
  };

  useEffect(() => {
    loadDoctors();
  }, []);

  const sortedDoctors = useMemo(() => {
    return [...doctors].sort((a, b) => {
      const aTime = new Date(a.created_at).getTime();
      const bTime = new Date(b.created_at).getTime();
      return bTime - aTime;
    });
  }, [doctors]);

  const totalPages = Math.max(1, Math.ceil(sortedDoctors.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const pagedDoctors = sortedDoctors.slice(startIndex, startIndex + pageSize);

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Just now';
    const diffMs = Date.now() - new Date(timestamp).getTime();
    if (Number.isNaN(diffMs)) return 'Recently';
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  };

  // Navigate to preview page to view doctor's posters
  const handleViewDoctor = (doctor) => {
    navigate('/preview', { 
      state: { 
        doctor: doctor,
        previewUrlV1: doctor.preview_url_v1 || doctor.poster_url_v1,
        posterUrlV1: doctor.poster_url_v1,
        previewUrlV6: doctor.preview_url_v6 || doctor.poster_url_v6,
        posterUrlV6: doctor.poster_url_v6,
        previewUrl: doctor.preview_url,
        posterUrl: doctor.poster_url
      }
    });
  };

  // ===========================================
  // DOWNLOAD HANDLER - Used by hidden download button
  // Uncomment the download button in JSX to enable
  // ===========================================
  const handleDownload = async (doctor) => {
    // Download both posters - using same logic as PreviewPage
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in to download');
      return;
    }
    
    const safeName = doctor.name?.replace(/\s+/g, '_') || 'doctor';
    
    const downloadFile = async (version, filename) => {
      // Silent retry - try up to 3 times with 2 second delay
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          console.log(`Download attempt ${attempt} for ${version}...`);
          
          const response = await fetch(`${API_BASE_URL}/doctors/${doctor.id}/download?version=${version}`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            const blob = await response.blob();
            if (blob.size > 0) {
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = filename;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
              console.log(`Download successful for ${version}`);
              return true;
            }
          }
        } catch (err) {
          console.log(`Download attempt ${attempt} failed for ${version}:`, err);
        }

        // Wait before retry
        if (attempt < 3) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
      return false;
    };
    
    // Download both posters sequentially with delay between
    // V6 (Cosmic Profile) first
    await downloadFile('v6', `cosmic_profile_dr_${safeName}.jpg`);
    // Wait 1 second then download V1 (Star Map)
    await new Promise(resolve => setTimeout(resolve, 1000));
    await downloadFile('v1', `star_map_dr_${safeName}.jpg`);
  };

  return (
    <div className="container">
      {/* Custom Confirmation Modal */}
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
        onConfirm={modalConfig.onConfirm}
        onCancel={closeModal}
      />
      
      <div className="doctor-list-header">
        <button
          className="btn btn-primary"
          onClick={() => navigate('/form')}
          disabled={sortedDoctors.length >= MAX_DOCTORS}
          title={sortedDoctors.length >= MAX_DOCTORS ? `Maximum limit of ${MAX_DOCTORS} doctors reached` : 'Add new doctor'}
          style={{ opacity: sortedDoctors.length >= MAX_DOCTORS ? 0.5 : 1 }}
        >
          ADD DOCTOR
        </button>
        <div className="doctor-list-total" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          fontSize: '16px',
          fontWeight: '600'
        }}>
          <span>Doctor Count:</span>
          <span style={{ 
            backgroundColor: sortedDoctors.length >= MAX_DOCTORS ? '#dc3545' : '#1f84f1',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '14px'
          }}>
            {sortedDoctors.length}/{MAX_DOCTORS}
          </span>
        </div>
      </div>

      {loading && (
        <div className="card" style={{ textAlign: 'center' }}>
          Loading doctors...
        </div>
      )}

      {error && <p className="error-message">{error}</p>}

      {!loading && !error && sortedDoctors.length === 0 && (
        <div className="card" style={{ textAlign: 'center' }}>
          No doctor profiles yet. Click "Add Doctor" to create one.
        </div>
      )}

      {!loading && !error && sortedDoctors.length > 0 && (
        <div className="doctor-list">
          {pagedDoctors.map((doctor) => (
            <div className="doctor-card" key={doctor.id}>
              <div className="doctor-card-info">
                <div className="doctor-card-title">Dr. {doctor.name}</div>
                <div className="doctor-card-subtitle">Cosmic Profile</div>
                <div className="doctor-card-time">{formatRelativeTime(doctor.created_at)}</div>
              </div>
              <div className="doctor-card-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                <button
                  className="btn action-btn"
                  onClick={() => handleViewDoctor(doctor)}
                  style={{ 
                    minWidth: '80px',
                    padding: '10px 14px',
                    fontSize: '13px',
                    borderRadius: '8px',
                    backgroundColor: '#17a2b8',
                    color: '#fff',
                    border: 'none'
                  }}
                >
                  👁️ VIEW
                </button>
                <button
                  className="btn btn-secondary action-btn"
                  onClick={() => navigate('/form', { state: { editDoctor: doctor } })}
                  disabled={doctor.is_approved}
                  title={doctor.is_approved ? 'Cannot edit approved doctor' : 'Edit doctor'}
                  style={{ 
                    minWidth: '80px',
                    padding: '10px 14px',
                    fontSize: '13px',
                    borderRadius: '8px',
                    opacity: doctor.is_approved ? 0.5 : 1
                  }}
                >
                  ✏️ EDIT
                </button>
                {/* 
                  ===========================================
                  DOWNLOAD BUTTON - HIDDEN (uncomment to enable)
                  ===========================================
                <button
                  className="btn btn-primary action-btn"
                  onClick={() => handleDownload(doctor)}
                  style={{ 
                    minWidth: '80px',
                    padding: '10px 14px',
                    fontSize: '13px',
                    borderRadius: '8px'
                  }}
                >
                  📥 DOWNLOAD
                </button>
                */}
                {doctor.is_approved ? (
                  <span 
                    style={{ 
                      minWidth: '80px',
                      padding: '10px 14px', 
                      backgroundColor: '#28a745', 
                      color: 'white', 
                      borderRadius: '8px',
                      fontWeight: '600',
                      fontSize: '13px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    ✓ APPROVED
                  </span>
                ) : (
                  <button
                    className="btn action-btn"
                    onClick={() => handleApprove(doctor)}
                    disabled={approvingId === doctor.id}
                    style={{ 
                      minWidth: '80px',
                      padding: '10px 14px',
                      fontSize: '13px',
                      borderRadius: '8px',
                      backgroundColor: '#f0ad4e', 
                      color: '#fff',
                      border: 'none'
                    }}
                  >
                    {approvingId === doctor.id ? '...' : '✓ APPROVE'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && sortedDoctors.length > pageSize && (
        <div className="pagination">
          <button
            className="btn btn-secondary"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={safePage === 1}
          >
            Prev
          </button>
          <div className="pagination-info">
            Page {safePage} of {totalPages}
          </div>
          <button
            className="btn btn-secondary"
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={safePage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      <button
        className="btn btn-secondary"
        style={{ marginTop: '16px' }}
        onClick={() => navigate('/welcome')}
      >
        HOME
      </button>
    </div>
  );
};

export default DoctorListPage;
